import React, { ReactNode } from 'react';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useMSALAuth } from '../../contexts/MSALAuthContext';
import { MSALLogin } from './MSALLogin';
import { AuthError } from './AuthError';
import { ENG_STRING_CONSTS } from '../../constants';

interface MSALProtectedRouteProps {
  children: ReactNode;
}

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

export const MSALProtectedRoute: React.FC<MSALProtectedRouteProps> = ({ children }) => {
  const styles = useStyles();
  const { isAuthenticated, isLoading, isAuthReady, user, error } = useMSALAuth();

  // Show error state first if there's an error
  if (error) {
    return <AuthError type={error.type} message={error.message} details={error.details} />;
  }

  // Wait for authentication to be ready OR still loading
  if (isLoading || !isAuthReady) {
    return (
      <div className={styles.loadingContainer} data-testid="auth-msal-protected-route-loading">
        <Spinner size="large" data-testid="auth-msal-protected-route-loading-spinner" />
        <div data-testid="auth-msal-protected-route-loading-text">Loading authentication...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <MSALLogin />;
  }

  // CRITICAL FIX: Properly check Teams Admin role with boolean logic
  // Now that isTeamsAdmin is guaranteed to be boolean, this check is reliable
  if (user && user.isTeamsAdmin === false) {
    console.warn('User does not have Teams Admin role:', {
      user: user.email,
      isTeamsAdmin: user.isTeamsAdmin
    });
    
    return <AuthError 
      type="PERMISSION_DENIED" 
      details={ENG_STRING_CONSTS.ADMIN_ROLE_REQUIRED} 
    />;
  }

  return <>{children}</>;
};